import {Trans} from '@lingui/macro';
import memoize from 'lru-memoize';
import React, {Component} from 'react';

import {
  EventHandler,
  FrontEvent,
  HandlerOf,
} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SearchTagModel} from '../../../../../models/api/searchTagModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TeamModel} from '../../../../../models/api/teamModel';
import {ResourceMap} from '../../../../../store/appTypes';
import {DropdownCategory} from '../../../../common/dropdowns/dropdownCategory';
import {DropdownItemAccentIcon} from '../../../../common/dropdowns/dropdownItemAccentIcon';
import {PermissionDropdownItem} from '../../../../common/dropdowns/permissionDropdownItem';
import {DeprecatedSearchDropdown} from '../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {AsyncDropdownSource, DropdownFetcher} from '../../../../common/dropdowns/source/asyncDropdownSource';
import {DropdownSource, RowRendererOf} from '../../../../common/dropdowns/source/dropdownSource';
import {TagDropdownItem} from '../../../../common/dropdowns/tagDropdownItem';

/*
 * Props.
 */

interface ComposerTagsDropdownProps extends DropdownProps {
  teams: ResourceMap<TeamModel>;
  userTeams: ReadonlyArray<TeamModel>;
  fetchTagsAsync: DropdownFetcher<SearchTagModel | null>;
  onCreateTagSelect: EventHandler;
  tagMessage: HandlerOf<SearchTagModel>;
  untagMessage: HandlerOf<TagModel>;
  selectedTagIds: ReadonlyArray<number>;
}

/*
 * Component.
 */

export class ComposerTagsDropdown extends Component<ComposerTagsDropdownProps> {
  constructor(props: ComposerTagsDropdownProps) {
    super(props);

    const defaultRows = [
      null, // "New" title
      null, // "Tag" create item
      null, // "All Tags" title
    ];

    this.dropdownSource = new AsyncDropdownSource({
      fetcher: props.fetchTagsAsync,
      renderRow: this.renderRow,
      initialItems: defaultRows,
    });
  }

  /** The async search dropdown source for retrieving tags. */
  private readonly dropdownSource: DropdownSource;

  /*
   * Event handlers.
   */

  private readonly onToggleTag = (event: FrontEvent, tag: SearchTagModel, isChecked: boolean) => {
    event.preventDefault();

    if (isChecked) {
      this.props.untagMessage(tag[0]);
    } else {
      this.props.tagMessage(tag);
    }
  };

  /*
   * Render.
   */

  render() {
    return (
      <DeprecatedSearchDropdown
        {...this.props}
        title={<Trans id="dropdown-tags-header">Tags</Trans>}
        source={this.dropdownSource}
      />
    );
  }

  private readonly renderRow: RowRendererOf<SearchTagModel | null> = (
    searchTag,
    index,
    tags,
    isSearching,
  ) => {
    // If the tag is null, this is a fixed row.
    if (!searchTag) {
      return this.renderFixedRow(index);
    }

    // If we make the assumption that null values occupy the beginning slots as placeholders,
    // and there are none present, then we've applied filters and we shouldn't offset the index
    // to account for the two non-selectable titles (New, All Tags). PB-6124 should improve this.
    const useOffset = tags[0] === null;
    const adjustedIndex = index - (useOffset ? 2 : 0);
    return this.renderTagRow(searchTag, adjustedIndex, tags, isSearching);
  };

  private renderFixedRow(index: number) {
    switch (index) {
      case 0:
        return (
          <DropdownCategory>
            <Trans id="dropdown-tags-section-create">Create</Trans>
          </DropdownCategory>
        );

      case 1:
        // TODO: Check tag editing permissions.
        return (
          <PermissionDropdownItem onSelect={this.props.onCreateTagSelect} index={0}>
            <DropdownItemAccentIcon label={null} iconName="dropdownNewTag" />
            <DropdownItemContent>
              <Trans id="dropdown-tags-item-newtag">New tag</Trans>
            </DropdownItemContent>
          </PermissionDropdownItem>
        );

      case 2:
        return (
          <DropdownCategory>
            <Trans id="dropdown-tags-section-list">All tags</Trans>
          </DropdownCategory>
        );

      default:
        return null;
    }
  }

  private renderTagRow(
    searchTag: SearchTagModel,
    index: number,
    searchTags: ReadonlyArray<SearchTagModel | null>,
    isSearching?: boolean,
  ) {
    const {selectedTagIds, teams} = this.props;
    const tag = searchTag[0];
    const tagAliasDuplicates = getTagAliasDuplicates(searchTags);

    const isChecked = selectedTagIds.includes(tag.id);
    const team = teams[tag.namespace];
    const nestingLevel = isSearching ? 0 : searchTag.length - 1;
    const tagsPath = isSearching ? searchTag : [];

    return (
      <TagDropdownItem
        tag={tag}
        tagsPath={tagsPath}
        team={team}
        key={tag.id}
        index={index}
        isChecked={isChecked}
        nestingLevel={nestingLevel}
        shouldShowDeduplication={tagAliasDuplicates.has(tag.alias)}
        onSelect={(e) => this.onToggleTag(e, searchTag, isChecked)}
      />
    );
  }
}

/*
 * Helpers.
 */

const getTagAliasDuplicates = memoize()((searchTags: ReadonlyArray<SearchTagModel | null>) => {
  const tagAliases = new Set<string>();
  const tagAliasDuplicates = new Set<string>();

  // Loop on all the tags to find the ones with duplicate names.
  searchTags.forEach((searchTag) => {
    if (!searchTag) {
      return;
    }

    const tag = searchTag[0];
    if (tagAliases.has(tag.alias)) {
      tagAliasDuplicates.add(tag.alias);
      return;
    }

    tagAliases.add(tag.alias);
  });

  return tagAliasDuplicates;
});
